// GuestImport.jsx — right-aligned full-height CSV/XLSX import panel
// Steps: 1 Upload → 2 Map fields → 3 Preview → 4 Summary
// Upsert logic: unique on phone OR email (backend enforces)
import React, { useState, useRef } from "react";
import { Spinner } from "react-bootstrap";
import { read, utils } from "xlsx";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";

const C = {
  brand:"#4f46e5", brandLt:"#eef2ff",
  green:"#059669", greenLt:"#ecfdf5",
  text:"#111827", muted:"#6b7280", subtle:"#9ca3af",
  border:"#e5e7eb", bg:"#f9fafb", white:"#ffffff",
  danger:"#ef4444", amber:"#d97706",
};

// Fields we support — maps display label → DB column
const FIELD_MAP = [
  { field:"name",         label:"Full Name",           required:true  },
  { field:"phone",        label:"Phone Number",        required:true  },
  { field:"email",        label:"Email",               required:false },
  { field:"dob",          label:"Date of Birth",       required:false },
  { field:"anniversary",  label:"Marriage Anniversary",required:false },
  { field:"room_number",  label:"Room Number",         required:false },
  { field:"checkin_date", label:"Check-in Date",       required:false },
  { field:"checkout_date",label:"Check-out Date",      required:false },
  { field:"source",       label:"Source",              required:false },
  { field:"status",       label:"Status",              required:false },
  { field:"tags",         label:"Tags (comma sep.)",   required:false },
  { field:"notes",        label:"Notes",               required:false },
];

const DATE_FIELDS = ["dob","anniversary","checkin_date","checkout_date"];

const Lbl = ({children}) => (
  <div style={{fontSize:"0.67rem",fontWeight:800,textTransform:"uppercase",
    letterSpacing:"0.07em",color:C.subtle,marginBottom:5}}>
    {children}
  </div>
);

const IMPORT_MODES = [
  { value:"upsert", label:"Add & Update",      desc:"Add new + update existing by phone/email" },
  { value:"insert", label:"Add New Only",       desc:"Skip rows that match existing guests" },
  { value:"update", label:"Update Existing",    desc:"Only update guests already in the system" },
];

const GuestImport = ({ show, onClose, onSuccess }) => {
  const [step,        setStep]        = useState(0); // 0=upload 1=map 2=preview 3=result
  const [importMode,  setImportMode]  = useState("upsert");
  const [file,        setFile]        = useState(null);
  const [headers,     setHeaders]     = useState([]);   // CSV column headers
  const [rawRows,     setRawRows]     = useState([]);   // raw 2D array from file
  const [mapping,     setMapping]     = useState({});   // { dbField: csvHeader }
  const [importing,   setImporting]   = useState(false);
  const [result,      setResult]      = useState(null);
  const [error,       setError]       = useState("");
  const dropRef = useRef();

  if (!show) return null;

  // ── File parse ──────────────────────────────────────────────────────────────
  const parseFile = (f) => {
    setError("");
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["csv","xls","xlsx"].includes(ext)) {
      setError("Only CSV, XLS, XLSX files are supported."); return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb  = read(new Uint8Array(e.target.result), { type:"array" });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const all = utils.sheet_to_json(ws, { header:1 });
        const hdrs = (all[0] || []).map(String);
        const rows = all.slice(1).filter(r => r.some(Boolean));
        setHeaders(hdrs);
        setRawRows(rows);
        setFile(f);

        // Auto-map by fuzzy match
        const auto = {};
        FIELD_MAP.forEach(({field, label}) => {
          const match = hdrs.find(h =>
            h.toLowerCase().replace(/[\s_\-]/g,"") ===
            label.toLowerCase().replace(/[\s_\-]/g,"") ||
            h.toLowerCase().replace(/[\s_\-]/g,"") ===
            field.toLowerCase().replace(/[\s_\-]/g,"")
          );
          if (match) auto[field] = match;
        });
        setMapping(auto);
        setStep(1);
      } catch {
        setError("Could not parse the file. Please check the format.");
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) parseFile(f);
  };

  const onFileChange = (e) => {
    if (e.target.files[0]) parseFile(e.target.files[0]);
  };

  // ── Build import rows from mapping ─────────────────────────────────────────
  const buildRows = () => rawRows.map(row => {
    const obj = {};
    FIELD_MAP.forEach(({field}) => {
      const col = mapping[field];
      if (!col) return;
      const idx = headers.indexOf(col);
      let val = idx >= 0 ? row[idx] : undefined;
      if (val === undefined || val === null || val === "") return;
      val = String(val).trim();
      if (DATE_FIELDS.includes(field) && val) {
        // Normalise date to YYYY-MM-DD
        const d = new Date(val);
        if (!isNaN(d)) val = d.toISOString().split("T")[0];
      }
      if (field === "tags") val = val.split(",").map(t=>t.trim()).filter(Boolean);
      obj[field] = val;
    });
    return obj;
  }).filter(r => r.name || r.phone || r.email);

  // ── Execute import ──────────────────────────────────────────────────────────
  const doImport = async () => {
    setImporting(true); setError("");
    const rows = buildRows();
    if (!rows.length) { setError("No valid rows to import."); setImporting(false); return; }
    try {
      const res = await axios.post(
        `${apiUrl}/feedback/guests/import`,
        { guests: rows, mode: importMode },
        authHeaders()
      );
      setResult(res.data);
      setStep(3);
      onSuccess?.();
    } catch(err) {
      setError(err.response?.data?.error || "Import failed. Check your data and try again.");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStep(0); setFile(null); setHeaders([]); setRawRows([]);
    setMapping({}); setResult(null); setError("");
  };

  const previewRows = buildRows().slice(0, 5);
  const mappedFields = FIELD_MAP.filter(f => mapping[f.field]);

  // ── Step labels ─────────────────────────────────────────────────────────────
  const STEPS = ["Upload File","Map Fields","Preview","Result"];

  return (
    <div style={{position:"fixed",inset:0,zIndex:1070,display:"flex",alignItems:"stretch"}}>
      {/* Backdrop */}
      <div onClick={onClose}
        style={{flex:1,background:"rgba(0,0,0,0.4)",cursor:"pointer"}} />

      {/* Panel */}
      <div style={{width:580,flexShrink:0,background:C.white,
        display:"flex",flexDirection:"column",height:"100vh",
        boxShadow:"-8px 0 40px rgba(0,0,0,0.15)"}}>

        {/* Header */}
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${C.border}`,
          flexShrink:0,display:"flex",alignItems:"center",
          justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:"1rem",fontWeight:800,color:C.text}}>
              Import Guests
            </div>
            <div style={{fontSize:"0.73rem",color:C.subtle,marginTop:1}}>
              CSV · XLS · XLSX — upsert by phone or email
            </div>
          </div>
          <button onClick={onClose}
            style={{width:32,height:32,borderRadius:"50%",border:`1px solid ${C.border}`,
              background:C.bg,cursor:"pointer",display:"flex",
              alignItems:"center",justifyContent:"center",
              fontSize:"1.1rem",color:C.muted}}>
            ×
          </button>
        </div>

        {/* Step progress */}
        <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,
          flexShrink:0,background:C.bg}}>
          {STEPS.map((s,i) => (
            <div key={s} style={{flex:1,textAlign:"center",padding:"10px 0",
              fontSize:"0.72rem",fontWeight: i===step ? 800 : 500,
              color: i===step ? C.brand : i<step ? C.green : C.subtle,
              borderBottom: i===step ? `2.5px solid ${C.brand}` : "2.5px solid transparent"}}>
              <div style={{width:20,height:20,borderRadius:"50%",
                background: i<step ? C.green : i===step ? C.brand : C.border,
                color:C.white,fontSize:"0.65rem",fontWeight:800,
                display:"inline-flex",alignItems:"center",justifyContent:"center",
                marginBottom:3}}>
                {i<step ? "✓" : i+1}
              </div>
              <div>{s}</div>
            </div>
          ))}
        </div>

        {/* Scrollable body */}
        <div style={{flex:1,overflowY:"auto",padding:"20px 24px"}}>

          {/* Import mode */}
          {step < 3 && (
            <div style={{marginBottom:18}}>
              <Lbl>Import mode</Lbl>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {IMPORT_MODES.map(m => (
                  <label key={m.value}
                    style={{display:"flex",alignItems:"center",gap:10,
                      padding:"9px 12px",borderRadius:9,cursor:"pointer",
                      border:`1.5px solid ${importMode===m.value ? C.brand : C.border}`,
                      background: importMode===m.value ? C.brandLt : C.white}}>
                    <input type="radio" value={m.value} checked={importMode===m.value}
                      onChange={()=>setImportMode(m.value)} />
                    <div>
                      <div style={{fontSize:"0.82rem",fontWeight:700,color:C.text}}>{m.label}</div>
                      <div style={{fontSize:"0.72rem",color:C.muted}}>{m.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div style={{padding:"10px 14px",borderRadius:8,
              background:"#fef2f2",border:"1px solid #fecaca",
              color:C.danger,fontSize:"0.82rem",marginBottom:14,
              display:"flex",alignItems:"center",gap:8}}>
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          {/* STEP 0: Upload */}
          {step === 0 && (
            <div>
              <div ref={dropRef}
                onDrop={onDrop}
                onDragOver={e=>e.preventDefault()}
                onClick={()=>document.getElementById("gi_file_input").click()}
                style={{border:`2px dashed ${C.border}`,borderRadius:14,
                  padding:"40px 20px",textAlign:"center",cursor:"pointer",
                  transition:"all 0.15s",background:C.bg}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.brand;e.currentTarget.style.background=C.brandLt;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.bg;}}>
                <div style={{fontSize:"2.5rem",marginBottom:10}}>📂</div>
                <div style={{fontSize:"0.9rem",fontWeight:700,color:C.text,marginBottom:6}}>
                  Drag & drop your file here
                </div>
                <div style={{fontSize:"0.8rem",color:C.muted,marginBottom:14}}>
                  or click to browse — CSV, XLS, XLSX supported
                </div>
                <span style={{padding:"7px 18px",borderRadius:8,background:C.brand,
                  color:C.white,fontSize:"0.8rem",fontWeight:700}}>
                  Choose File
                </span>
                <input id="gi_file_input" type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={onFileChange}
                  style={{display:"none"}} />
              </div>

              {/* Sample CSV download */}
              <div style={{marginTop:16,padding:"12px 14px",borderRadius:10,
                background:"#f0fdf4",border:"1px solid #bbf7d0",
                display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{fontSize:"0.78rem",color:C.green}}>
                  <i className="fas fa-download" style={{marginRight:6}} />
                  Download a sample CSV to see the expected format
                </div>
                <a href="data:text/csv;charset=utf-8,name,phone,email,dob,anniversary,tags,notes%0ARahul Sharma,+919876543210,rahul@example.com,1990-01-15,2018-06-20,VIP,Regular guest%0APriya Mehta,+918765432109,priya@example.com,1985-07-22,,Loyalty,Anniversary next month"
                  download="guest_import_sample.csv"
                  style={{fontSize:"0.76rem",fontWeight:700,color:C.green,
                    textDecoration:"none",padding:"5px 12px",borderRadius:7,
                    border:`1px solid ${C.green}`,background:C.white}}>
                  Download Sample
                </a>
              </div>
            </div>
          )}

          {/* STEP 1: Map fields */}
          {step === 1 && (
            <div>
              <div style={{display:"flex",alignItems:"center",
                justifyContent:"space-between",marginBottom:14}}>
                <div style={{fontSize:"0.84rem",fontWeight:700,color:C.text}}>
                  {file?.name} — {rawRows.length} rows detected
                </div>
                <button onClick={()=>{
                  const auto = {};
                  FIELD_MAP.forEach(({field,label}) => {
                    const match = headers.find(h =>
                      h.toLowerCase().replace(/[\s_\-]/g,"") ===
                      label.toLowerCase().replace(/[\s_\-]/g,"") ||
                      h.toLowerCase().replace(/[\s_\-]/g,"") ===
                      field.toLowerCase().replace(/[\s_\-]/g,"")
                    );
                    if (match) auto[field] = match;
                  });
                  setMapping(auto);
                }} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${C.brand}`,
                  background:C.brandLt,color:C.brand,fontSize:"0.76rem",
                  fontWeight:700,cursor:"pointer"}}>
                  Auto-Map
                </button>
              </div>

              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {FIELD_MAP.map(({field,label,required}) => (
                  <div key={field} style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:180,flexShrink:0,fontSize:"0.8rem",
                      fontWeight:600,color:C.text}}>
                      {label}
                      {required && <span style={{color:C.danger,marginLeft:3}}>*</span>}
                    </div>
                    <select value={mapping[field]||""}
                      onChange={e=>setMapping(p=>({...p,[field]:e.target.value||undefined}))}
                      style={{flex:1,padding:"7px 10px",fontSize:"0.8rem",
                        border:`1px solid ${mapping[field] ? C.brand : C.border}`,
                        borderRadius:8,outline:"none",
                        background: mapping[field] ? C.brandLt : C.white,
                        color:C.text}}>
                      <option value="">— Not mapped —</option>
                      {headers.map(h=>(
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    {mapping[field] && (
                      <i className="fas fa-check-circle"
                        style={{color:C.green,fontSize:"0.82rem",flexShrink:0}} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Preview */}
          {step === 2 && (
            <div>
              <div style={{fontSize:"0.84rem",fontWeight:700,color:C.text,marginBottom:10}}>
                Preview — first 5 rows of {rawRows.length} total
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",
                  fontSize:"0.76rem"}}>
                  <thead>
                    <tr style={{background:C.bg}}>
                      {mappedFields.map(f=>(
                        <th key={f.field} style={{padding:"7px 10px",
                          textAlign:"left",fontWeight:700,color:C.subtle,
                          borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row,i)=>(
                      <tr key={i} style={{background: i%2===0 ? C.white : C.bg}}>
                        {mappedFields.map(f=>(
                          <td key={f.field} style={{padding:"7px 10px",
                            color:C.text,borderBottom:`1px solid ${C.border}`}}>
                            {Array.isArray(row[f.field])
                              ? row[f.field].join(", ")
                              : row[f.field] || <span style={{color:C.border}}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{marginTop:14,padding:"10px 14px",borderRadius:8,
                background:"#f0f9ff",border:"1px solid #bae6fd",
                fontSize:"0.78rem",color:"#0369a1"}}>
                <strong>{rawRows.length} rows</strong> will be imported.{" "}
                Mode: <strong>{IMPORT_MODES.find(m=>m.value===importMode)?.label}</strong>{" — "}
                unique match on <strong>phone or email</strong>.
              </div>
            </div>
          )}

          {/* STEP 3: Result */}
          {step === 3 && result && (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{width:64,height:64,borderRadius:"50%",
                background: result.errors?.length ? "#fef2f2" : C.greenLt,
                display:"flex",alignItems:"center",justifyContent:"center",
                margin:"0 auto 16px",fontSize:"1.8rem"}}>
                {result.errors?.length ? "⚠️" : "✅"}
              </div>
              <div style={{fontSize:"1rem",fontWeight:800,color:C.text,marginBottom:6}}>
                {result.errors?.length ? "Import completed with errors" : "Import successful!"}
              </div>

              <div style={{display:"flex",gap:16,justifyContent:"center",marginBottom:20}}>
                {[
                  {label:"Total",   val:result.total   || rawRows.length, color:C.text},
                  {label:"Inserted",val:result.inserted || 0,             color:C.green},
                  {label:"Updated", val:result.updated  || 0,             color:C.brand},
                  {label:"Skipped", val:result.skipped  || 0,             color:C.muted},
                  {label:"Errors",  val:result.errors?.length || 0,       color:C.danger},
                ].map(({label,val,color})=>(
                  <div key={label} style={{textAlign:"center"}}>
                    <div style={{fontSize:"1.4rem",fontWeight:800,color}}>{val}</div>
                    <div style={{fontSize:"0.68rem",color:C.subtle,
                      textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
                  </div>
                ))}
              </div>

              {result.errors?.length > 0 && (
                <div style={{textAlign:"left",marginBottom:16}}>
                  <div style={{fontSize:"0.78rem",fontWeight:700,color:C.danger,marginBottom:8}}>
                    Error details:
                  </div>
                  <div style={{maxHeight:180,overflowY:"auto"}}>
                    {result.errors.map((e,i)=>(
                      <div key={i} style={{fontSize:"0.74rem",padding:"5px 10px",
                        background:"#fef2f2",borderRadius:6,marginBottom:4,color:C.danger}}>
                        Row {e.row}: {e.reason || e.message || JSON.stringify(e)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fixed footer */}
        <div style={{padding:"14px 24px",borderTop:`1px solid ${C.border}`,
          flexShrink:0,background:C.white,
          display:"flex",gap:10,justifyContent:"flex-end"}}>

          {step > 0 && step < 3 && (
            <button onClick={()=>setStep(s=>s-1)}
              style={{padding:"9px 18px",borderRadius:9,
                border:`1px solid ${C.border}`,background:C.white,
                color:C.text,fontSize:"0.84rem",fontWeight:600,cursor:"pointer"}}>
              ← Back
            </button>
          )}

          {step === 0 && (
            <button onClick={onClose}
              style={{padding:"9px 18px",borderRadius:9,
                border:`1px solid ${C.border}`,background:C.white,
                color:C.text,fontSize:"0.84rem",fontWeight:600,cursor:"pointer"}}>
              Cancel
            </button>
          )}

          {step === 1 && (
            <button onClick={()=>{
              const missing = FIELD_MAP
                .filter(f => f.required && !mapping[f.field])
                .map(f=>f.label);
              const hasContact = mapping["phone"] || mapping["email"];
              if (!hasContact) { setError("Map at least Phone or Email."); return; }
              setError(""); setStep(2);
            }}
              style={{padding:"9px 20px",borderRadius:9,border:"none",
                background:C.brand,color:C.white,fontSize:"0.84rem",
                fontWeight:700,cursor:"pointer"}}>
              Next: Preview →
            </button>
          )}

          {step === 2 && (
            <button onClick={doImport} disabled={importing}
              style={{padding:"9px 20px",borderRadius:9,border:"none",
                background: importing ? "#a5b4fc" : C.brand,
                color:C.white,fontSize:"0.84rem",fontWeight:700,
                cursor: importing ? "not-allowed":"pointer",
                display:"flex",alignItems:"center",gap:8}}>
              {importing
                ? <><Spinner animation="border" size="sm" /> Importing…</>
                : <>Import {rawRows.length} guests</>}
            </button>
          )}

          {step === 3 && (
            <>
              <button onClick={reset}
                style={{padding:"9px 18px",borderRadius:9,
                  border:`1px solid ${C.border}`,background:C.white,
                  color:C.text,fontSize:"0.84rem",fontWeight:600,cursor:"pointer"}}>
                Import Another
              </button>
              <button onClick={onClose}
                style={{padding:"9px 20px",borderRadius:9,border:"none",
                  background:C.brand,color:C.white,fontSize:"0.84rem",
                  fontWeight:700,cursor:"pointer"}}>
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestImport;
